import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
    Typography, Box, Button, Stack, LinearProgress,
    Alert, Snackbar, Chip, IconButton, Dialog, DialogTitle, DialogContent, 
    DialogActions, Fade, Zoom, TextField, MenuItem, InputAdornment, Divider,
    List, ListItemButton, ListItemText, ListItemIcon
} from "@mui/material";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import {
    VisibilityOutlined as ViewIcon,
    CheckCircleOutline as FinalizeIcon,
    DeleteOutline as VoidIcon,
    Refresh as RefreshIcon,
    Print as PrintIcon,
    FileDownload as DownloadIcon,
    ReceiptLongOutlined as PayslipIcon,
    PendingActionsOutlined as PendingIcon,
    TaskAltOutlined as ApprovedIcon,
    WarningAmberOutlined as AttentionIcon,
    PaymentsOutlined as PaymentsIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { myanmarFontBase64 } from "../../utils/myanmarFontBase64";
import { useTenantContext } from "../../context/TenantContext";
import { buildHierarchicalList } from "../../components/staff/constants";
import EffectiveDatedPayrollBreakdown from '../../components/payroll/EffectiveDatedPayrollBreakdown';
import PayslipPreviewDialog from '../../components/payroll/PayslipPreviewDialog';

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const money = value => Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
});

const PayrollDraftReview = ({ setIsLoggedIn }) => {
    const [loading, setLoading] = useState(false);
    const [runs, setRuns] = useState([]);
    const [selectedRun, setSelectedRun] = useState(null);
    const [payslips, setPayslips] = useState([]);
    const [branches, setBranches] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportFilters, setExportFilters] = useState({ employee_search: '', branch_id: '', org_node_id: '' });
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });
    const [breakdownPayroll, setBreakdownPayroll] = useState(null);
    const [statusFilter, setStatusFilter] = useState('PENDING');
    const [payslipPreviewIndex, setPayslipPreviewIndex] = useState(null);
    const [pendingStatusAction, setPendingStatusAction] = useState(null);
    const [statusComment, setStatusComment] = useState('');
    const { tenantProfile } = useTenantContext();
    const tenant = useMemo(() => localStorage.getItem('tenant') || '', []);

    const fetchRuns = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/runs`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setRuns(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching runs:", error);
            setStatusMsg({ open: true, msg: 'Error fetching payroll runs', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [tenant]);

    useEffect(() => {
        fetchRuns();
    }, [fetchRuns]);

    useEffect(() => {
        const fetchFilterMasters = async () => {
            try {
                const token = localStorage.getItem('token');
                const config = {
                    headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                    withCredentials: true
                };
                const [branchRes, nodeRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, config),
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, config)
                ]);
                setBranches(Array.isArray(branchRes.data?.data) ? branchRes.data.data : []);
                setDepartments(Array.isArray(nodeRes.data?.data) ? buildHierarchicalList(nodeRes.data.data) : []);
            } catch (error) {
                console.error("Error fetching payslip export filters:", error);
            }
        };

        if (tenant) fetchFilterMasters();
    }, [tenant]);

    const handleViewDetails = async (run) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/runs/${run.id}/payslips`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setPayslips(response.data.data);
                setSelectedRun(run);
            }
        } catch (error) {
            console.error("Error fetching payslips:", error);
            setStatusMsg({ open: true, msg: 'Error fetching details', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const requestStatusUpdate = (run, status) => {
        setStatusComment('');
        setPendingStatusAction({ run, status });
    };

    const handleUpdateStatus = async () => {
        if (!pendingStatusAction) return;
        const { run, status } = pendingStatusAction;
        if (['REJECTED', 'VOID'].includes(status) && !statusComment.trim()) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${process.env.REACT_APP_API_URL1}/sm/payroll/runs/${run.id}/status`, { status, comment: statusComment.trim() }, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setStatusMsg({ open: true, msg: `Run ${status.toLowerCase()} successfully`, severity: 'success' });
                fetchRuns();
                setSelectedRun(null);
                setPendingStatusAction(null);
                setStatusComment('');
            }
        } catch (error) {
            console.error("Error updating status:", error);
            setStatusMsg({ open: true, msg: error.response?.data?.message || 'Error updating status', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleRecalculate = async (runId) => {
        const reason = window.prompt('Reason for recalculating this payroll draft:') || '';
        if (!reason.trim()) return;
        setLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/runs/${runId}/recalculate`, { reason }, { withCredentials: true, headers: { 'X-Tenant': tenant } });
            setStatusMsg({ open: true, msg: response.data.message, severity: 'success' });
            setSelectedRun(null);
            fetchRuns();
        } catch (error) {
            setStatusMsg({ open: true, msg: error.response?.data?.message || 'Recalculation failed', severity: 'error' });
        } finally { setLoading(false); }
    };

    const generatePayslipsPDF = async (runData, payslipRecords) => {
        try {
            const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a5", compress: true });
            const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
            const periodName = `${monthNames[runData.period_month - 1]} ${runData.period_year}`;

            const loadLogo = () => {
                return new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => resolve(img);
                    img.onerror = () => resolve(null);
                    img.src = tenantProfile?.logo_url || "/logo.png";
                });
            };

            const img = await loadLogo();
            const logoAlias = img ? "company_logo" : null;

            if (myanmarFontBase64) {
                doc.addFileToVFS("Padauk-Regular.ttf", myanmarFontBase64);
                doc.addFont("Padauk-Regular.ttf", "Padauk", "normal");
                doc.addFont("Padauk-Regular.ttf", "Padauk", "bold");
            }
            const defaultFont = myanmarFontBase64 ? "Padauk" : "helvetica";

            payslipRecords.forEach((slip, index) => {
                if (index > 0) doc.addPage();
                
                // Add Logo using alias to save space (reused across pages)
                if (img) {
                    try {
                        doc.addImage(img, 'JPEG', 14, 5, 30, 30, logoAlias, 'FAST');
                    } catch (e) {
                        console.warn("Logo failed to render in PDF", e);
                    }
                }

                doc.setFontSize(22);
                doc.setTextColor(tenantProfile?.primary_color || "#4338ca"); // Brand Primary
                doc.text("PAY ADVICE", 105, 20, { align: "center" });

                doc.setFontSize(10);
                doc.setTextColor(100);
                doc.text(`Period: ${periodName}`, 105, 28, { align: "center" });

                doc.setTextColor(15, 23, 42); // slate-900
                doc.setDrawColor(226, 232, 240); // slate-200
                doc.line(14, 40, 196, 40);

                doc.setFont(defaultFont, "bold");
                doc.text("Employee Name:", 14, 46);
                doc.setFont(defaultFont, "normal");
                doc.text(slip.full_name || '-', 48, 46);

                doc.setFont(defaultFont, "bold");
                doc.text("Employee Code:", 14, 52);
                doc.setFont(defaultFont, "normal");
                doc.text(slip.employee_number || slip.staff_code || '-', 48, 52);

                doc.setFont(defaultFont, "bold");
                doc.text("Organization:", 120, 46);
                doc.setFont(defaultFont, "normal");
                doc.text(slip.dept_name || 'N/A', 150, 46);

                doc.setFont(defaultFont, "bold");
                doc.text("Designation:", 120, 52);
                doc.setFont(defaultFont, "normal");
                doc.text(slip.job_title || 'N/A', 150, 52);

                doc.line(14, 58, 196, 58);

                const earnings = [];
                const salarySegments = slip.compensation_segments || [];
                if (salarySegments.length > 1) {
                    salarySegments.forEach(segment => {
                        earnings.push([
                            `Basic Salary (${String(segment.startDate).slice(0, 10)} – ${String(segment.endDate).slice(0, 10)})`,
                            Number(segment.basicPay ?? segment.exactBasicPay ?? 0).toLocaleString()
                        ]);
                    });
                } else if ((slip.basic_salary || 0) > 0) {
                    earnings.push(["Basic Salary", (slip.basic_salary || 0).toLocaleString()]);
                }

                let hasSplits = false;
                const hoursPerDay = slip.hours_per_day ? parseFloat(slip.hours_per_day) : 8;

                if ((slip.normal_ot_pay || 0) > 0) {
                    earnings.push([`Normal OT (${((slip.normal_ot_mins || 0) / 60).toFixed(2)} hrs)`, (slip.normal_ot_pay || 0).toLocaleString()]);
                    hasSplits = true;
                }
                if ((slip.rest_ot_pay || 0) > 0) {
                    const days = (((slip.rest_ot_mins || 0) / 60) / hoursPerDay).toFixed(2);
                    earnings.push([`Rest OT (${days} days)`, (slip.rest_ot_pay || 0).toLocaleString()]);
                    hasSplits = true;
                }
                if ((slip.off_day_ot_pay || 0) > 0) {
                    const days = (((slip.off_day_ot_mins || 0) / 60) / hoursPerDay).toFixed(2);
                    earnings.push([`Off OT (${days} days)`, (slip.off_day_ot_pay || 0).toLocaleString()]);
                    hasSplits = true;
                }
                if ((slip.holiday_ot_pay || 0) > 0) {
                    const days = (((slip.holiday_ot_mins || 0) / 60) / hoursPerDay).toFixed(2);
                    earnings.push([`Holiday OT (${days} days)`, (slip.holiday_ot_pay || 0).toLocaleString()]);
                    hasSplits = true;
                }
                if ((slip.extended_ot_pay || 0) > 0) {
                    earnings.push([`Extended OT (${((slip.extended_ot_mins || 0) / 60).toFixed(2)} hrs)`, (slip.extended_ot_pay || 0).toLocaleString()]);
                    hasSplits = true;
                }

                if (!hasSplits && (slip.ot_pay || 0) > 0) {
                    earnings.push([`OT Payment (${((slip.payable_ot_mins || 0) / 60).toFixed(2)} hrs)`, (slip.ot_pay || 0).toLocaleString()]);
                }

                if (slip.meal_allowance > 0) earnings.push(["Meal Allowance", slip.meal_allowance.toLocaleString()]);
                if (slip.transport_allowance > 0) earnings.push(["Transport Allowance", slip.transport_allowance.toLocaleString()]);
                if ((slip.special_allowance || 0) > 0) earnings.push(["Special Allowance", slip.special_allowance.toLocaleString()]);
                if ((slip.ot_allowance || 0) > 0) earnings.push(["OT Allowance", slip.ot_allowance.toLocaleString()]);
                
                (slip.recurring_items || []).filter(it => it.element_type === 'EARNING').forEach(it => {
                    if (parseFloat(it.amount) > 0) {
                        earnings.push([it.element_name || it.element_code, parseFloat(it.amount).toLocaleString()]);
                    }
                });

                const deductions = [];
                if ((slip.late_early_penalties || 0) > 0) {
                    deductions.push(["Attendance Penalty", (slip.late_early_penalties || 0).toLocaleString()]);
                }
                if ((slip.unpaid_leave_deduction || 0) > 0) {
                    deductions.push(["Unpaid Leave", (slip.unpaid_leave_deduction || 0).toLocaleString()]);
                }
                if ((slip.unpaid_absence_deduction || 0) > 0) {
                    deductions.push([`Unpaid Absence (${Number(slip.unpaid_absence_days || 0).toFixed(2)} days)`, (slip.unpaid_absence_deduction || 0).toLocaleString()]);
                }
                if ((parseFloat(slip.ssb_deduction || 0)) > 0) {
                    deductions.push(["Social Security", (parseFloat(slip.ssb_deduction || 0)).toLocaleString()]);
                }
                if ((parseFloat(slip.tax_deduction || 0)) > 0) {
                    deductions.push(["Income Tax", (parseFloat(slip.tax_deduction || 0)).toLocaleString()]);
                }
                if ((parseFloat(slip.advance_deduction || 0)) > 0) {
                    deductions.push([slip.is_advance ? "Previous Advances" : "Advance Payroll Paid", (parseFloat(slip.advance_deduction || 0)).toLocaleString()]);
                }
                
                (slip.recurring_items || []).filter(it => it.element_type === 'DEDUCTION').forEach(it => {
                    if (parseFloat(it.amount) > 0) {
                        deductions.push([it.element_name || it.element_code, parseFloat(it.amount).toLocaleString()]);
                    }
                });

                const grossPayVal = parseFloat(slip.gross_pay || 0);
                const netPayVal = parseFloat(slip.net_pay || 0);
                const totalDeductionsVal = Math.max(0, grossPayVal - netPayVal);

                autoTable(doc, {
                    startY: 62,
                    head: [['Earnings', 'Amount (MMK)']],
                    body: earnings,
                    foot: [['Total Gross Earnings', `${grossPayVal.toLocaleString()} MMK`]],
                    footStyles: { fillColor: [240, 253, 244], textColor: [22, 101, 52], font: defaultFont, fontStyle: 'bold' },
                    theme: 'striped',
                    headStyles: { fillColor: tenantProfile?.primary_color || "#4338ca", font: defaultFont },
                    margin: { left: 14, right: 110 },
                    styles: { fontSize: 8, font: defaultFont }
                });

                autoTable(doc, {
                    startY: 62,
                    head: [['Deductions', 'Amount (MMK)']],
                    body: deductions.length ? deductions : [['No deductions applied', '0']],
                    foot: [['Total Deductions', `${totalDeductionsVal.toLocaleString()} MMK`]],
                    footStyles: { fillColor: [255, 241, 242], textColor: [190, 18, 60], font: defaultFont, fontStyle: 'bold' },
                    theme: 'striped',
                    headStyles: { fillColor: [244, 63, 94], font: defaultFont }, // Rose-500
                    margin: { left: 110, right: 14 },
                    styles: { fontSize: 8, font: defaultFont }
                });

                const finalY = Math.max(doc.lastAutoTable.finalY, 110);
                doc.setDrawColor(203, 213, 225);
                doc.setFillColor(255, 255, 255);
                doc.roundedRect(14, finalY + 5, 182, 20, 2, 2, 'FD');

                doc.setFontSize(8);
                doc.setFont(defaultFont, 'bold');
                doc.setTextColor(71, 85, 105);
                doc.text('NET TAKE-HOME PAY', 20, finalY + 12);

                doc.setFontSize(7.5);
                doc.setFont(defaultFont, 'normal');
                doc.setTextColor(100, 116, 139);
                doc.text(`Gross (${grossPayVal.toLocaleString()}) - Deductions (${totalDeductionsVal.toLocaleString()})`, 20, finalY + 20);

                doc.setFontSize(12);
                doc.setFont(defaultFont, 'bold');
                doc.setTextColor(15, 23, 42);
                doc.text(`${netPayVal.toLocaleString()} MMK`, 190, finalY + 17, { align: 'right' });

                doc.setFontSize(7);
                doc.setTextColor(148, 163, 184);
                doc.text("System generated document. No physical signature required.", 105, 140, { align: "center" });
            });

            doc.save(`Payslips_${periodName}.pdf`);
        } catch (error) {
            console.error("PDF Export Crash:", error);
            setStatusMsg({ open: true, msg: `PDF Generation failed: ${error.message}`, severity: 'error' });
        }
    };

    const exportToCSV = (runData, payslipRecords) => {
        const dynamicAlwCols = new Set();
        const dynamicDedCols = new Set();
        payslipRecords.forEach(row => {
            Object.keys(row).forEach(k => {
                if (k.startsWith('alw_')) dynamicAlwCols.add(k);
                if (k.startsWith('ded_')) dynamicDedCols.add(k);
            });
        });
        const alwArr = Array.from(dynamicAlwCols);
        const dedArr = Array.from(dynamicDedCols);
        const headers = ["Employee Code", "Name", "Basic", "OT", ...alwArr.map(k => k.replace('alw_', '')), "Penalty", "SSB", "Tax", ...dedArr.map(k => k.replace('ded_', '') === 'MISSED_CLOCK_PENALTY' ? 'Missed Clock Fine' : k.replace('ded_', '')), "Net Pay"];
        const rows = payslipRecords.map(s => [
            s.employee_number || s.staff_code, s.full_name, s.basic_salary, s.ot_pay, ...alwArr.map(k => s[k] || 0),
            s.late_early_penalties, s.ssb_deduction, s.tax_deduction, ...dedArr.map(k => s[k] || 0), s.net_pay
        ]);
        let csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(e => e.join(",")).join("\n");
        const link = document.createElement("a");
        link.href = encodeURI(csvContent);
        link.download = `Payroll_Run_${runData.id}_${runData.period_month}_${runData.period_year}.csv`;
        link.click();
    };

    const filteredExportPayslips = useMemo(() => {
        const employeeSearch = String(exportFilters.employee_search || '').trim().toLowerCase();
        const branchId = String(exportFilters.branch_id || '');
        const orgNodeId = String(exportFilters.org_node_id || '');

        return payslips.filter((slip) => {
            const haystack = [
                slip.employee_number,
                slip.staff_code,
                slip.full_name
            ].filter(Boolean).join(' ').toLowerCase();

            if (employeeSearch && !haystack.includes(employeeSearch)) return false;
            if (branchId && String(slip.branch_id || '') !== branchId) return false;
            if (orgNodeId && String(slip.org_node_id || '') !== orgNodeId) return false;
            return true;
        });
    }, [exportFilters, payslips]);

    const employeeSearchMatches = useMemo(() => {
        const employeeSearch = String(exportFilters.employee_search || '').trim().toLowerCase();
        if (!employeeSearch) return [];
        return payslips.filter((slip) => {
            const haystack = [
                slip.employee_number,
                slip.staff_code,
                slip.full_name
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(employeeSearch);
        }).slice(0, 8);
    }, [exportFilters.employee_search, payslips]);

    const handleExportFilterChange = useCallback((field, value) => {
        setExportFilters(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleSelectExportEmployee = useCallback((slip) => {
        setExportFilters(prev => ({
            ...prev,
            employee_search: slip.employee_number || slip.staff_code || slip.full_name || ''
        }));
    }, []);

    const handleOpenExportDialog = useCallback(() => {
        setExportFilters({ employee_search: '', branch_id: '', org_node_id: '' });
        setExportDialogOpen(true);
    }, []);

    const handleFilteredExport = (format) => {
        if (!selectedRun) return;
        if (!filteredExportPayslips.length) {
            setStatusMsg({ open: true, msg: 'No payslips match the selected export filters', severity: 'warning' });
            return;
        }

        if (format === 'CSV') {
            exportToCSV(selectedRun, filteredExportPayslips);
        } else {
            generatePayslipsPDF(selectedRun, filteredExportPayslips);
        }
        setExportDialogOpen(false);
    };

    const handleExportJournal = async (runId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/accounting-export?run_id=${runId}&format=GENERIC_CSV`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true,
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Journal_Export_Run_${runId}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            
            setStatusMsg({ open: true, msg: 'Journal Exported successfully', severity: 'success' });
        } catch (error) {
            console.error("Error exporting journal:", error);
            setStatusMsg({ open: true, msg: 'Failed to export journal', severity: 'error' });
        }
    };

    const filteredRuns = useMemo(() => {
        if (statusFilter === 'ALL') return runs;
        if (statusFilter === 'PENDING') return runs.filter(run => ['DRAFT', 'SUBMITTED', 'REJECTED'].includes(run.status));
        return runs.filter(run => run.status === statusFilter);
    }, [runs, statusFilter]);

    const reviewSummary = useMemo(() => ({
        pending: runs.filter(run => ['DRAFT', 'SUBMITTED', 'REJECTED'].includes(run.status)).length,
        submitted: runs.filter(run => run.status === 'SUBMITTED').length,
        approved: runs.filter(run => run.status === 'APPROVED').length,
        attention: runs.filter(run => run.status === 'REJECTED' || run.integrity_status === 'STALE').length,
        pendingValue: runs.filter(run => ['DRAFT', 'SUBMITTED', 'APPROVED'].includes(run.status)).reduce((sum, run) => sum + Number(run.total_net_pay || 0), 0)
    }), [runs]);

    const selectedRunSummary = useMemo(() => payslips.reduce((summary, payslip) => ({
        gross: summary.gross + Number(payslip.gross_pay || 0),
        deductions: summary.deductions + Math.max(0, Number(payslip.gross_pay || 0) - Number(payslip.net_pay || 0)),
        net: summary.net + Number(payslip.net_pay || 0)
    }), { gross: 0, deductions: 0, net: 0 }), [payslips]);

    const openPayslipPreview = useCallback((payslip) => {
        const index = payslips.findIndex(item => item.id === payslip.id);
        setPayslipPreviewIndex(index >= 0 ? index : 0);
    }, [payslips]);

    const gridStyle = {
        '--ag-header-background-color': '#f1f5f9',
        '--ag-header-foreground-color': '#475569',
        '--ag-header-cell-font-weight': '800',
        '--ag-header-cell-height': '52px',
        '--ag-row-height': '52px',
        '--ag-font-family': 'Inter, system-ui, sans-serif',
        '--ag-font-size': '14px',
        '--ag-border-color': '#f1f5f9',
        '--ag-row-hover-color': '#f8fafc',
        '--ag-selected-row-color': (theme) => `${theme.palette.primary.main}33`,
        '--ag-border-radius': '16px'
    };

    const runColumnDefs = [
        {
            headerName: "Payroll Period", minWidth: 155, flex: 0.9,
            valueGetter: p => `${monthNames[Number(p.data.period_month) - 1] || p.data.period_month} ${p.data.period_year}`,
            cellStyle: { fontWeight: 800, color: '#0f172a' }
        },
        { field: "payroll_group_name", headerName: "Payroll Group", minWidth: 190, flex: 1.2, valueFormatter: p => p.value || 'Legacy / Unscoped' },
        { 
            field: "status", headerName: "Status", width: 190,
            cellRenderer: p => {
                const colors = { FINALIZED: 'success', PAID: 'success', APPROVED: 'info', SUBMITTED: 'info', DRAFT: 'warning', REJECTED: 'error', VOID: 'error' };
                return (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip label={p.value} size="small" color={colors[p.value]} sx={{ fontWeight: 700, fontSize: '0.65rem' }} />
                        {p.data.integrity_status === 'STALE' && <Chip label="STALE" size="small" color="error" variant="outlined" sx={{ fontWeight: 800, fontSize: '0.6rem' }} />}
                        {Number(p.data.released_staff_count || 0) === Number(p.data.staff_count || 0) && Number(p.data.staff_count || 0) > 0 && <Chip label="RELEASED" size="small" variant="outlined" color="primary" sx={{ fontWeight: 800, fontSize: '0.6rem', borderColor: 'primary.main', color: 'primary.main' }} />}
                        {Number(p.data.released_staff_count || 0) > 0 && Number(p.data.released_staff_count || 0) < Number(p.data.staff_count || 0) && <Chip label="PARTIAL" size="small" variant="outlined" color="warning" sx={{ fontWeight: 800, fontSize: '0.6rem' }} />}
                    </Stack>
                );
            }
        },
        { field: "staff_count", headerName: "Employees", width: 115, cellStyle: { fontWeight: 700 } },
        { field: "total_net_pay", headerName: "Net Disbursement", minWidth: 165, flex: 1, valueFormatter: p => `${money(p.value)} MMK`, cellStyle: { fontWeight: 900, color: '#0f172a' } },
        { field: "run_date", headerName: "Prepared", minWidth: 170, flex: 1, valueFormatter: p => new Date(p.value).toLocaleString() },
        { field: "current_revision", headerName: "Revision", width: 105, valueFormatter: p => `R${p.value || 1}${Number(p.data.correction_count || 0) > 0 ? ` + C${p.data.correction_count}` : ''}` },
        {
            headerName: "Review", width: 130, pinned: 'right', sortable: false, filter: false,
            cellRenderer: p => (
                <Stack direction="row" spacing={0.5} sx={{ height: '100%', alignItems: 'center' }}>
                    <Button size="small" variant="outlined" startIcon={<ViewIcon />} onClick={() => handleViewDetails(p.data)} sx={{ textTransform: 'none', fontWeight: 800, minWidth: 88 }}>Review</Button>
                    {p.data.status === 'DRAFT' && (
                        <>
                            <Tooltip title="Submit for approval"><IconButton size="small" color="primary" onClick={() => requestStatusUpdate(p.data, 'SUBMITTED')}><FinalizeIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Void"><IconButton size="small" color="error" onClick={() => requestStatusUpdate(p.data, 'VOID')}><VoidIcon fontSize="small" /></IconButton></Tooltip>
                        </>
                    )}
                    {p.data.status === 'SUBMITTED' && (
                        <>
                            <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => requestStatusUpdate(p.data, 'APPROVED')}><FinalizeIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Reject"><IconButton size="small" color="error" onClick={() => requestStatusUpdate(p.data, 'REJECTED')}><VoidIcon fontSize="small" /></IconButton></Tooltip>
                        </>
                    )}
                    {p.data.status === 'APPROVED' && <Tooltip title="Finalize"><IconButton size="small" color="success" onClick={() => requestStatusUpdate(p.data, 'FINALIZED')}><FinalizeIcon fontSize="small" /></IconButton></Tooltip>}
                </Stack>
            )
        }
    ];

    const payslipColumnDefs = useMemo(() => {
        const dynamicAlwCols = new Set();
        const dynamicDedCols = new Set();
        payslips.forEach(row => {
            Object.keys(row).forEach(k => {
                if (k.startsWith('alw_')) dynamicAlwCols.add(k);
                if (k.startsWith('ded_')) dynamicDedCols.add(k);
            });
        });
        const dynamicAdditions = Array.from(dynamicAlwCols).map(k => ({ field: k, headerName: k.replace('alw_', ''), width: 120, valueFormatter: p => parseFloat(p.value || 0).toLocaleString(), cellStyle: { color: '#059669' } }));
        const dynamicDeductions = Array.from(dynamicDedCols).map(k => {
            const raw = k.replace('ded_', '');
            const headerName = raw === 'MISSED_CLOCK_PENALTY' ? 'Missed Clock Fine' : raw;
            return { field: k, headerName, width: 140, valueFormatter: p => parseFloat(p.value || 0).toLocaleString(), cellStyle: { color: '#dc2626' } };
        });

        return [
            {
                headerName: "Payslip", width: 105, pinned: 'left', sortable: false, filter: false,
                cellRenderer: p => (
                    <Button size="small" startIcon={<PayslipIcon />} onClick={() => openPayslipPreview(p.data)} sx={{ textTransform: 'none', fontWeight: 800, px: 1 }}>
                        Preview
                    </Button>
                )
            },
            {
                headerName: "Audit", width: 82, pinned: 'left', sortable: false, filter: false,
                cellRenderer: p => (
                    <Tooltip title="Calculation breakdown">
                        <IconButton size="small" color="primary" onClick={() => setBreakdownPayroll(p.data)}>
                            <ViewIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )
            },
            { field: "employee_number", headerName: "Employee ID", width: 140, pinned: 'left', valueGetter: p => p.data.employee_number || p.data.staff_code || '-' },
            { field: "full_name", headerName: "Name", flex: 1, minWidth: 200, pinned: 'left' },
            { field: "basic_salary", headerName: "Basic", width: 110, valueFormatter: p => parseFloat(p.value || 0).toLocaleString() },
            { field: "ot_pay", headerName: "Total OT Pay", width: 120, valueFormatter: p => parseFloat(p.value || 0).toLocaleString(), cellStyle: { fontWeight: 'bold' } },
            { field: "normal_ot_mins", headerName: "Normal OT Min", width: 120 },
            { field: "normal_ot_pay", headerName: "Normal OT Pay", width: 120, valueFormatter: p => parseFloat(p.value || 0).toLocaleString() },
            { field: "rest_ot_mins", headerName: "Rest OT Min", width: 110 },
            { field: "holiday_ot_mins", headerName: "Holiday OT Min", width: 120 },
            { field: "holiday_ot_pay", headerName: "Holiday OT Pay", width: 120, valueFormatter: p => parseFloat(p.value || 0).toLocaleString() },
            { field: "extended_ot_mins", headerName: "Extended OT Min", width: 130 },
            { field: "extended_ot_pay", headerName: "Extended OT Pay", width: 130, valueFormatter: p => parseFloat(p.value || 0).toLocaleString() },
            ...dynamicAdditions,
            { field: "meal_allowance", headerName: "Meal Alw.", width: 100, valueFormatter: p => parseFloat(p.value || 0).toLocaleString(), cellStyle: { color: '#059669' } },
            { field: "transport_allowance", headerName: "Trans Alw.", width: 110, valueFormatter: p => parseFloat(p.value || 0).toLocaleString(), cellStyle: { color: '#059669' } },
            {
                field: "special_allowance",
                headerName: "Spec Alw.",
                width: 100,
                valueFormatter: p => p.value > 0 ? parseFloat(p.value).toLocaleString() : '-',
                cellStyle: p => p.value > 0 ? { color: '#059669', fontWeight: 'bold' } : { color: '#94a3b8' }
            },
            {
                field: "ot_allowance",
                headerName: "Shift OT Alw.",
                width: 120,
                valueFormatter: p => p.value > 0 ? parseFloat(p.value).toLocaleString() : '-',
                cellStyle: p => p.value > 0 ? { color: '#2e7d32', fontWeight: 'bold' } : { color: '#94a3b8' }
            },
            { field: "late_early_penalties", headerName: "Penalty", width: 110, valueFormatter: p => parseFloat(p.value || 0).toLocaleString(), cellStyle: { color: '#ef4444' } },
            { field: "ssb_deduction", headerName: "SSB", width: 100, valueFormatter: p => parseFloat(p.value || 0).toLocaleString(), cellStyle: { color: '#ef4444' } },
            { field: "tax_deduction", headerName: "PIT", width: 100, valueFormatter: p => parseFloat(p.value || 0).toLocaleString(), cellStyle: { color: '#ef4444' } },
            { field: "advance_deduction", headerName: "Advance Deduction", width: 145, valueFormatter: p => parseFloat(p.value || 0).toLocaleString(), cellStyle: { color: '#ef4444' } },
            ...dynamicDeductions,
            { field: "net_pay", headerName: "Net Payable", width: 130, valueFormatter: p => parseFloat(p.value || 0).toLocaleString(), cellStyle: { fontWeight: 800, color: 'primary.main', bgcolor: (theme) => `${theme.palette.primary.main}33` } },
        ];
    }, [openPayslipPreview, payslips]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 8 }}>
            <AppHeader onLogout={() => { localStorage.clear(); setIsLoggedIn(false); window.location.href='/login'; }} />
            
            <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 3 }}>
                <Fade in timeout={600}>
                    <Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 1 }}>Payroll Review &amp; Approval</Typography>
                                <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>Review, approve and finalize payroll runs</Typography>
                            </Box>
                            <Button 
                                variant="contained" 
                                startIcon={<RefreshIcon />} 
                                onClick={fetchRuns} 
                                sx={{ 
                                    bgcolor: '#fff', 
                                    color: theme => theme.palette.primary.main, 
                                    '&:hover': { bgcolor: '#f1f5f9' }, 
                                    fontWeight: 700, 
                                    borderRadius: 3, 
                                    px: 4, 
                                    py: 1.5,
                                    border: '1px solid #e2e8f0', 
                                    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
                                    textTransform: 'none'
                                }}
                            >
                                Refresh Data
                            </Button>
                        </Stack>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' }, gap: 1.5, mb: 2.5 }}>
                            {[
                                { label: 'Pending Review', value: reviewSummary.pending, icon: PendingIcon, color: '#3730a3', bg: '#eef2ff' },
                                { label: 'Submitted', value: reviewSummary.submitted, icon: PendingIcon, color: '#0369a1', bg: '#f0f9ff' },
                                { label: 'Approved', value: reviewSummary.approved, icon: ApprovedIcon, color: '#166534', bg: '#f0fdf4' },
                                { label: 'Needs Attention', value: reviewSummary.attention, icon: AttentionIcon, color: '#b91c1c', bg: '#fef2f2' },
                                { label: 'Pending Value', value: `${money(reviewSummary.pendingValue)} MMK`, icon: PaymentsIcon, color: '#166534', bg: '#f0fdf4' }
                            ].map(item => (
                                <Paper key={item.label} variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: item.bg, borderColor: '#e2e8f0' }}>
                                    <Stack direction="row" spacing={1.25} alignItems="center">
                                        <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#fff', color: item.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{React.createElement(item.icon, { sx: { fontSize: 20 } })}</Box>
                                        <Box sx={{ minWidth: 0 }}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>{item.label}</Typography><Typography variant="h6" sx={{ color: item.color, fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.value}</Typography></Box>
                                    </Stack>
                                </Paper>
                            ))}
                        </Box>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                            {[
                                { key: 'PENDING', label: `Pending (${reviewSummary.pending})` },
                                { key: 'SUBMITTED', label: `Submitted (${reviewSummary.submitted})` },
                                { key: 'APPROVED', label: `Approved (${reviewSummary.approved})` },
                                { key: 'FINALIZED', label: `Finalized (${runs.filter(run => run.status === 'FINALIZED').length})` },
                                { key: 'ALL', label: `All Runs (${runs.length})` }
                            ].map(filter => <Chip key={filter.key} label={filter.label} onClick={() => setStatusFilter(filter.key)} color={statusFilter === filter.key ? 'primary' : 'default'} variant={statusFilter === filter.key ? 'filled' : 'outlined'} sx={{ fontWeight: 800, cursor: 'pointer' }} />)}
                        </Stack>

                        <Paper sx={{ p: 0, borderRadius: 5, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 10px 30px -12px rgba(0,0,0,0.08)' }}>
                            <Box className="ag-theme-quartz" sx={{ height: 560, width: '100%', ...gridStyle }}>
                                <AgGridReact
                                    theme="legacy"
                                    rowData={filteredRuns}
                                    columnDefs={runColumnDefs} 
                                    defaultColDef={{ 
                                        resizable: true, 
                                        sortable: true, 
                                        filter: true,
                                        suppressMovable: true
                                    }} 
                                    loadingOverlayComponentParams={{ loadingMessage: 'Syncing history...' }}
                                    animateRows={true}
                                />
                                {loading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, bgcolor: 'transparent', '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }} />}
                            </Box>
                        </Paper>
                    </Box>
                </Fade>
            </Box>

            <Dialog open={!!selectedRun} onClose={() => setSelectedRun(null)} maxWidth="lg" fullWidth TransitionComponent={Fade} PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ p: 3, bgcolor: '#f8fafc' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>{monthNames[Number(selectedRun?.period_month) - 1]} {selectedRun?.period_year} Payroll</Typography>
                                <Chip label={selectedRun?.status} size="small" color={selectedRun?.status === 'REJECTED' || selectedRun?.status === 'VOID' ? 'error' : ['FINALIZED', 'PAID'].includes(selectedRun?.status) ? 'success' : 'primary'} sx={{ fontWeight: 900 }} />
                            </Stack>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Run #{selectedRun?.id} • {selectedRun?.payroll_group_name || 'Payroll Group'} • Revision R{selectedRun?.current_revision || 1}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button variant="contained" startIcon={<PayslipIcon />} onClick={() => setPayslipPreviewIndex(0)} disabled={payslips.length === 0} sx={{ borderRadius: 2, fontWeight: 800, textTransform: 'none' }}>Preview Payslips</Button>
                            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleExportJournal(selectedRun.id)} sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', color: '#059669', borderColor: '#059669' }}>Journal</Button>
                            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleOpenExportDialog} sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}>CSV</Button>
                            <Button variant="outlined" startIcon={<PrintIcon />} onClick={handleOpenExportDialog} sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}>Export Payslips</Button>
                        </Stack>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.25, bgcolor: '#fff' }}>
                        {[
                            { label: 'Employees', value: payslips.length },
                            { label: 'Gross Payroll', value: `${money(selectedRunSummary.gross)} MMK` },
                            { label: 'Deductions', value: `${money(selectedRunSummary.deductions)} MMK` },
                            { label: 'Net Disbursement', value: `${money(selectedRunSummary.net)} MMK` }
                        ].map(metric => <Box key={metric.label} sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800 }}>{metric.label.toUpperCase()}</Typography><Typography variant="subtitle1" sx={{ color: '#0f172a', fontWeight: 900 }}>{metric.value}</Typography></Box>)}
                    </Box>
                    <Box className="ag-theme-quartz" sx={{ height: 430, width: '100%', ...gridStyle }}>
                        <AgGridReact theme="legacy" rowData={payslips} columnDefs={payslipColumnDefs} defaultColDef={{ resizable: true, sortable: true, filter: true }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setSelectedRun(null)} sx={{ color: '#64748b', fontWeight: 700 }}>Dismiss</Button>
                    {selectedRun?.status === 'FINALIZED' && <Button variant="outlined" color="success" startIcon={<FinalizeIcon />} onClick={() => requestStatusUpdate(selectedRun, 'PAID')} sx={{ borderRadius: 2, fontWeight: 700 }}>Mark Paid</Button>}
                    {selectedRun?.status === 'DRAFT' && (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={() => handleRecalculate(selectedRun.id)} sx={{ borderRadius: 2, fontWeight: 700 }}>Recalculate</Button>
                            <Button variant="outlined" color="error" startIcon={<VoidIcon />} onClick={() => requestStatusUpdate(selectedRun, 'VOID')} sx={{ borderRadius: 2, fontWeight: 700 }}>Void Cycle</Button>
                            <Button variant="contained" color="primary" startIcon={<FinalizeIcon />} onClick={() => requestStatusUpdate(selectedRun, 'SUBMITTED')} sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}>Submit for Approval</Button>
                        </Stack>
                    )}
                    {selectedRun?.status === 'SUBMITTED' && (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" color="error" startIcon={<VoidIcon />} onClick={() => requestStatusUpdate(selectedRun, 'REJECTED')} sx={{ borderRadius: 2, fontWeight: 700 }}>Reject</Button>
                            <Button variant="contained" color="success" startIcon={<FinalizeIcon />} onClick={() => requestStatusUpdate(selectedRun, 'APPROVED')} sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}>Approve Payroll</Button>
                        </Stack>
                    )}
                    {selectedRun?.status === 'REJECTED' && (
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={() => handleRecalculate(selectedRun.id)} sx={{ borderRadius: 2, fontWeight: 700 }}>Recalculate</Button>
                            <Button variant="outlined" color="error" startIcon={<VoidIcon />} onClick={() => requestStatusUpdate(selectedRun, 'VOID')} sx={{ borderRadius: 2, fontWeight: 700 }}>Void Cycle</Button>
                            <Button variant="contained" color="warning" startIcon={<RefreshIcon />} onClick={() => requestStatusUpdate(selectedRun, 'DRAFT')} sx={{ borderRadius: 2, fontWeight: 700 }}>Return to Draft</Button>
                        </Stack>
                    )}
                    {selectedRun?.status === 'APPROVED' && <Button variant="contained" color="success" startIcon={<FinalizeIcon />} onClick={() => requestStatusUpdate(selectedRun, 'FINALIZED')} sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}>Finalize Payroll</Button>}
                </DialogActions>
            </Dialog>

            <PayslipPreviewDialog
                open={payslipPreviewIndex !== null && Boolean(selectedRun)}
                payslip={payslipPreviewIndex !== null ? payslips[payslipPreviewIndex] : null}
                run={selectedRun}
                position={payslipPreviewIndex || 0}
                total={payslips.length}
                onClose={() => setPayslipPreviewIndex(null)}
                onPrevious={() => setPayslipPreviewIndex(index => Math.max(0, Number(index || 0) - 1))}
                onNext={() => setPayslipPreviewIndex(index => Math.min(payslips.length - 1, Number(index || 0) + 1))}
            />

            <Dialog open={Boolean(pendingStatusAction)} onClose={() => !loading && setPendingStatusAction(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 900 }}>
                        Confirm {({ SUBMITTED: 'Submission', APPROVED: 'Payroll Approval', REJECTED: 'Rejection', DRAFT: 'Return to Draft', FINALIZED: 'Payroll Finalization', PAID: 'Payment Status', VOID: 'Void Payroll Run' })[pendingStatusAction?.status] || 'Status Change'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>Review the financial scope before continuing.</Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, p: 2, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
                            <Box><Typography variant="caption" color="text.secondary" fontWeight={800}>PAYROLL PERIOD</Typography><Typography variant="body1" fontWeight={900}>{monthNames[Number(pendingStatusAction?.run?.period_month) - 1]} {pendingStatusAction?.run?.period_year}</Typography></Box>
                            <Box><Typography variant="caption" color="text.secondary" fontWeight={800}>EMPLOYEES</Typography><Typography variant="body1" fontWeight={900}>{pendingStatusAction?.run?.staff_count || 0}</Typography></Box>
                            <Box sx={{ gridColumn: '1 / -1' }}><Typography variant="caption" color="text.secondary" fontWeight={800}>NET DISBURSEMENT</Typography><Typography variant="h5" color="primary.main" fontWeight={900}>{money(pendingStatusAction?.run?.total_net_pay)} MMK</Typography></Box>
                        </Box>
                    </Box>
                    {['REJECTED', 'VOID'].includes(pendingStatusAction?.status) && (
                        <TextField label={`${pendingStatusAction?.status === 'REJECTED' ? 'Rejection' : 'Void'} reason`} value={statusComment} onChange={event => setStatusComment(event.target.value)} multiline minRows={3} required fullWidth sx={{ mt: 2.5 }} helperText="A reason is required for the audit history." />
                    )}
                    {['APPROVED', 'FINALIZED', 'PAID'].includes(pendingStatusAction?.status) && (
                        <Alert severity={pendingStatusAction?.status === 'FINALIZED' ? 'warning' : 'info'} sx={{ mt: 2.5 }}>
                            {pendingStatusAction?.status === 'FINALIZED' ? 'Finalization makes this the official payroll result. Verify the payslip preview and total disbursement first.' : 'This action will be recorded in the payroll workflow history.'}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setPendingStatusAction(null)} disabled={loading} sx={{ color: '#64748b', fontWeight: 700 }}>Cancel</Button>
                    <Button variant="contained" color={['REJECTED', 'VOID'].includes(pendingStatusAction?.status) ? 'error' : 'primary'} onClick={handleUpdateStatus} disabled={loading || (['REJECTED', 'VOID'].includes(pendingStatusAction?.status) && !statusComment.trim())} sx={{ fontWeight: 800, px: 3 }}>
                        Confirm Action
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!breakdownPayroll} onClose={() => setBreakdownPayroll(null)} maxWidth="lg" fullWidth>
                <DialogTitle>Payroll Calculation — {breakdownPayroll?.full_name || breakdownPayroll?.employee_number || ''}</DialogTitle>
                <DialogContent dividers><EffectiveDatedPayrollBreakdown payroll={breakdownPayroll} /></DialogContent>
                <DialogActions><Button onClick={() => setBreakdownPayroll(null)}>Close</Button></DialogActions>
            </Dialog>

            <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth TransitionComponent={Fade} PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ p: 3, bgcolor: '#f8fafc' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>Export Payslips</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                        Filter the payslips for cycle {selectedRun?.period_month}/{selectedRun?.period_year}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    <Stack spacing={2.5}>
                        <TextField
                            label="Employee Code / Name"
                            value={exportFilters.employee_search}
                            onChange={(event) => handleExportFilterChange('employee_search', event.target.value)}
                            placeholder="Type employee code or name"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <ViewIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                        {exportFilters.employee_search.trim() && (
                            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden', bgcolor: '#fff', mt: -1 }}>
                                {employeeSearchMatches.length > 0 ? (
                                    <List dense disablePadding>
                                        {employeeSearchMatches.map(slip => (
                                            <ListItemButton key={slip.id} onClick={() => handleSelectExportEmployee(slip)} sx={{ py: 1 }}>
                                                <ListItemIcon sx={{ minWidth: 42 }}>
                                                    <Chip label={slip.employee_number || slip.staff_code || '-'} size="small" color="primary" variant="outlined" sx={{ fontWeight: 800 }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={slip.full_name || '-'}
                                                    secondary={[slip.branch_name, slip.dept_name].filter(Boolean).join(' • ') || 'No branch/department'}
                                                    primaryTypographyProps={{ fontWeight: 800, color: '#0f172a' }}
                                                    secondaryTypographyProps={{ color: '#64748b', fontWeight: 600 }}
                                                />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography variant="body2" sx={{ p: 2, color: '#64748b', fontWeight: 700 }}>
                                        No matching employee found in this payroll run.
                                    </Typography>
                                )}
                            </Box>
                        )}
                        <TextField
                            label="Branch"
                            value={exportFilters.branch_id}
                            onChange={(event) => handleExportFilterChange('branch_id', event.target.value)}
                            select
                            fullWidth
                        >
                            <MenuItem value="">All Branches</MenuItem>
                            {branches.map(branch => (
                                <MenuItem key={branch.id} value={String(branch.id)}>{branch.branch_name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="SOM / Department"
                            value={exportFilters.org_node_id}
                            onChange={(event) => handleExportFilterChange('org_node_id', event.target.value)}
                            select
                            fullWidth
                        >
                            <MenuItem value="">All Departments</MenuItem>
                            {departments.map(node => (
                                <MenuItem key={node.id} value={String(node.id)} sx={{ pl: 2 + (node._depth || 0) * 2 }}>
                                    {node.node_name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Divider />
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>Matched Payslips</Typography>
                                <Typography variant="h5" sx={{ color: '#0f172a', fontWeight: 900 }}>{filteredExportPayslips.length}</Typography>
                            </Box>
                            <Chip
                                label={filteredExportPayslips.length === payslips.length ? 'All payslips' : 'Filtered export'}
                                color={filteredExportPayslips.length === payslips.length ? 'default' : 'primary'}
                                variant={filteredExportPayslips.length === payslips.length ? 'outlined' : 'filled'}
                                sx={{ fontWeight: 800 }}
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setExportDialogOpen(false)} sx={{ color: '#64748b', fontWeight: 700 }}>Cancel</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleFilteredExport('CSV')} disabled={!filteredExportPayslips.length} sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}>Export CSV</Button>
                    <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handleFilteredExport('PDF')} disabled={!filteredExportPayslips.length} sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}>Export PDF</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={statusMsg.open} autoHideDuration={6000} onClose={() => setStatusMsg({ ...statusMsg, open: false })} TransitionComponent={Zoom}>
                <Alert severity={statusMsg.severity} variant="filled" sx={{ width: '100%', borderRadius: 3, fontWeight: 600 }}>{statusMsg.msg}</Alert>
            </Snackbar>
        </Box>
    );
};

export default PayrollDraftReview;
